import type { ReactNode } from 'react';
import { Loader2, ShieldAlert } from 'lucide-react';
import { Banner, Card } from '@/components/ui';
import { SetupProgress } from '@/components/geo/SetupProgress';
import { useSiteSetup } from '@/hooks/useGeo';
import { useNodes } from '@/hooks/useNodes';
import type { JobDetail, SiteDetail } from '@/types/api';
import { failureDetails, failureText } from '@/utils/errors';

/**
 * What the site is doing or why it stopped, above everything else: during an
 * incident this is the sentence the page was opened for.
 */
export function SiteStateBanner({ site, jobs }: { readonly site: SiteDetail; readonly jobs: readonly JobDetail[] }): ReactNode {
  const failed = site.status === 'failed';
  // Only to name the servers a failed placement refused.
  const nodes = useNodes({ enabled: failed });
  // A site being made shows how far it has got, stage by stage (ADR-0047).
  const setup = useSiteSetup(site.status === 'provisioning' ? site.id : null);

  const moving = jobs.find((job) => job.type === 'site.migrate' && ['queued', 'running', 'retrying'].includes(job.state));
  if (moving) {
    return (
      <Banner tone="info" icon={Loader2} spin>
        Сайт переносится на другой сервер и находится в режиме обслуживания.
      </Banner>
    );
  }

  if (site.status === 'provisioning') {
    return (
      <Card title="Создание сайта" icon={Loader2}>
        <SetupProgress setup={setup.data} />
      </Card>
    );
  }

  if (site.status === 'destroying') {
    return (
      <Banner tone="warning" icon={Loader2} spin>
        Сайт удаляется с сервера.
      </Banner>
    );
  }

  if (failed) {
    const creation = jobs.find((job) => job.type === 'site.create' && job.state === 'failed');
    const nodeName = (nodeId: string): string => nodes.data?.find((node) => node.id === nodeId)?.name ?? `сервер ${nodeId.slice(0, 8)}`;
    const details = failureDetails(creation?.errorDetails ?? null, nodeName);

    return (
      <Banner tone="danger" icon={ShieldAlert}>
        <div className="stack stack-tight">
          <span className="strong">Сайт не удалось создать</span>
          {site.healthReasons.map((reason) => (
            <span key={reason} title={reason}>
              {failureText(reason)}
            </span>
          ))}
          {details.map((line) => (
            <span key={line} className="text-sm">
              {line}
            </span>
          ))}
          <span className="text-sm muted">Сайт можно удалить без резервной копии.</span>
        </div>
      </Banner>
    );
  }

  if (site.healthReasons.length === 0) return null;

  return (
    <Banner tone="warning" icon={ShieldAlert}>
      <div className="stack stack-tight">
        {site.healthReasons.map((reason) => (
          <span key={reason} title={reason}>
            {failureText(reason)}
          </span>
        ))}
      </div>
    </Banner>
  );
}
